// App.js

import React, { lazy, Suspense, useEffect, useRef } from 'react';
import { Route, Routes } from 'react-router-dom';
import ReactGA from 'react-ga4';
import { ConfigProvider, theme as antdTheme } from 'antd';
import 'antd/dist/reset.css';
import './i18n';

import { CompanyProvider } from '@contexts/companyContext';
import { LanguageProvider, useLanguage } from './contexts/LanguageContext';

import ThemeStyles from '@styles/theme';
import './style.scss';
import 'react-toastify/dist/ReactToastify.min.css';
import 'react-grid-layout/css/styles.css';

import { SidebarProvider } from '@contexts/sidebarContext';
import { ThemeProvider } from 'styled-components';
import { ModalProvider } from '@contexts/modalContext';
import { AuthProvider } from './contexts/authContext';
import { useThemeProvider } from '@contexts/themeContext';

import { ToastContainer } from 'react-toastify';
import Loader from '@components/Loader';
import BramaPanelu from './panelv2/BramaPanelu';

const SignIn = lazy(() => import('@pages/SignIn'));
const SignUp = lazy(() => import('@pages/SignUp'));
const Verification = lazy(() => import('@pages/Verification'));
const Zaproszenie = lazy(() => import('@pages/Zaproszenie'));
// Powroty ze Stripe (/subscription-success, /subscription-cancelled) obsługuje
// PanelV2App — bez nich klient po obciążeniu karty trafiał na nieistniejącą
// ścieżkę i widział pusty ekran.

const App = () => {
    const appRef = useRef(null);
    const { theme } = useThemeProvider();
    const { locale } = useLanguage();

    useEffect(() => {
        appRef.current && appRef.current.scrollTo(0, 0);
    }, []);

    const gaKey = process.env.REACT_APP_PUBLIC_GOOGLE_ANALYTICS;
    gaKey && ReactGA.initialize(gaKey);

    return (

        <ConfigProvider
            theme={{
                // Без darkAlgorithm компоненты antd (модалки, селекты) в тёмной
                // теме панели оставались белыми и выбивались из интерфейса.
                algorithm: theme === 'dark' ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
                token: {
                    colorPrimary: '#E0F64B',
                    borderRadius: 10,
                },
            }}
            locale={locale}
        >
            
            <AuthProvider>
                <CompanyProvider>
                    <SidebarProvider>
                        <ThemeProvider theme={{ theme: theme }}>
                            <ThemeStyles />
                            <ToastContainer theme="colored" autoClose={2000} toastStyle={{ borderRadius: 4 }} />
                            <ModalProvider>
                                {/* Ekrany publiczne zostają tutaj — panel jest
                                    dla zalogowanych, a na /sign-in trafia się
                                    właśnie stamtąd (ProtectedRoute). Reszta
                                    adresów należy do Panelu v2, jedynego
                                    panelu produktu — patrz BramaPanelu. */}
                                <Suspense fallback={<Loader visible pelnyEkran />}>
                                    <Routes>
                                        <Route path="/sign-in" element={<SignIn />} />
                                        <Route path="/sign-up" element={<SignUp />} />
                                        <Route path="/verification" element={<Verification />} />
                                        <Route path="/zaproszenie" element={<Zaproszenie />} />
                                        <Route path="*" element={<BramaPanelu />} />
                                    </Routes>
                                </Suspense>
                            </ModalProvider>
                        </ThemeProvider>
                    </SidebarProvider>
                </CompanyProvider>
            </AuthProvider>
        </ConfigProvider>
    );
}

const AppWrapper = () => (
    <LanguageProvider>
        <App />
    </LanguageProvider>
);

export default AppWrapper;
